import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Hexagon, FileText, Tags, FileCheck, BellRing, Database, FolderCog,
  Check, ArrowRight, ArrowLeft, MessageCircle, Phone, ChevronRight,
} from "lucide-react";
import { getServices } from "@/lib/siteContent";
import { useLanguage } from "@/context/LanguageContext";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

const ICONS = { Hexagon, FileText, Tags, FileCheck, BellRing, Database, FolderCog };

const ServiceDetail = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const SERVICES = getServices(language);
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/hizmetler" replace />;

  const Icon = ICONS[service.icon] || Hexagon;
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <div data-testid="service-detail-page" className="bg-white">
      {/* Hero */}
      <section className="relative pt-20 pb-12 lg:pt-28 lg:pb-16 bg-white">
        <div className="absolute inset-0 hex-pattern opacity-100 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-slate-500 mb-6">
            <Link to="/" className="hover:text-teal-700">{t("breadcrumb.home")}</Link>
            <ChevronRight size={12} />
            <Link to="/hizmetler" className="hover:text-teal-700">{t("breadcrumb.services")}</Link>
            <ChevronRight size={12} />
            <span className="text-slate-700">{service.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-100 text-teal-800 text-xs font-medium">
                <Icon size={14} /> {service.tagline || t("serviceDetail.badgeFallback")}
              </div>
              <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">
                {service.title}
              </h1>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
                {service.description}
              </p>

              <div className="mt-7 flex flex-wrap gap-2">
                {service.highlights.map((h) => (
                  <span
                    key={h}
                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs text-slate-700"
                  >
                    {h}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <img
                src={service.image}
                alt={service.title}
                className="w-full rounded-2xl object-cover aspect-[5/4] shadow-xl shadow-teal-900/10"
                data-testid="service-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-24" data-testid="service-body">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main content */}
          <div className="lg:col-span-8 space-y-10">
            {service.sections.map((sec, i) => (
              <article key={i} data-testid={`service-section-${i}`}>
                <h2 className="font-display text-2xl lg:text-3xl text-slate-900 tracking-tight">
                  {sec.heading}
                </h2>
                <div className="mt-4 space-y-4">
                  {(sec.paragraphs || []).map((p, idx) => (
                    <p key={idx} className="text-slate-700 leading-[1.85]">{p}</p>
                  ))}
                  {sec.bullets && (
                    <ul className="mt-2 space-y-2.5">
                      {sec.bullets.map((b, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-slate-700">
                          <Check size={16} className="text-teal-700 mt-1 shrink-0" />
                          <span className="leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            ))}

            {/* FAQ */}
            {service.faqs?.length > 0 && (
              <div className="pt-6" data-testid="service-faqs">
                <h2 className="font-display text-2xl lg:text-3xl text-slate-900 tracking-tight">
                  {t("serviceDetail.faqTitle")}
                </h2>
                <Accordion type="single" collapsible className="mt-5">
                  {service.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200">
                      <AccordionTrigger className="text-left text-slate-900 hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-700 leading-relaxed">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-5">
            <div className="p-6 rounded-2xl border border-slate-200 bg-white sticky top-24">
              <div className="text-xs uppercase tracking-[0.2em] text-teal-700">{t("serviceDetail.sidebarKicker")}</div>
              <h3 className="mt-2 font-display text-2xl text-slate-900">{t("serviceDetail.sidebarTitle")}</h3>
              <p className="mt-2 text-sm text-slate-600">
                {t("serviceDetail.sidebarLead")}
              </p>
              <div className="mt-5 space-y-2.5">
                <Link
                  to="/iletisim"
                  className="w-full inline-flex items-center justify-between gap-2 px-5 h-12 rounded-full bg-teal-700 text-white text-sm font-medium hover:bg-teal-800 transition-colors"
                  data-testid="service-cta-quote"
                >
                  {t("serviceDetail.quoteForm")} <ArrowRight size={15} />
                </Link>
                <a
                  href="https://wa.me/902123333333"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-between gap-2 px-5 h-12 rounded-full border border-slate-200 text-slate-800 text-sm font-medium hover:border-teal-700 hover:text-teal-700 transition-colors"
                  data-testid="service-cta-whatsapp"
                >
                  {t("serviceDetail.whatsapp")} <MessageCircle size={15} />
                </a>
                <a
                  href="tel:+902128120295"
                  className="w-full inline-flex items-center justify-between gap-2 px-5 h-12 rounded-full border border-slate-200 text-slate-800 text-sm font-medium hover:border-teal-700 hover:text-teal-700 transition-colors"
                  data-testid="service-cta-phone"
                >
                  0212 812 02 95 <Phone size={15} />
                </a>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">{t("serviceDetail.scopeKicker")}</div>
                <ul className="space-y-2 text-sm text-slate-700">
                  {service.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2">
                      <Check size={14} className="text-teal-700 mt-1" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Other services */}
      <section className="py-16 lg:py-20 border-t border-slate-100 bg-[#F0FDFA]" data-testid="service-related">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-2">{t("serviceDetail.relatedKicker")}</div>
              <h2 className="font-display text-3xl lg:text-4xl text-slate-900 tracking-tight">
                {t("serviceDetail.relatedTitle")}
              </h2>
            </div>
            <Link
              to="/hizmetler"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800"
              data-testid="service-all-link"
            >
              {t("serviceDetail.allServices")} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {otherServices.map((s, idx) => {
              const SIcon = ICONS[s.icon] || Hexagon;
              return (
                <Link
                  key={s.slug}
                  to={`/hizmetler/${s.slug}`}
                  className="p-6 rounded-xl bg-white border border-slate-200 card-hover"
                  data-testid={`related-service-${idx}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                    <SIcon size={18} />
                  </div>
                  <h3 className="mt-4 font-display text-lg text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2">{s.short}</p>
                </Link>
              );
            })}
          </div>

          <div className="mt-10">
            <Link
              to="/hizmetler"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-teal-700"
              data-testid="back-to-services"
            >
              <ArrowLeft size={14} /> {t("serviceDetail.backToServices")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
